<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">实名认证</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ list.length }}</b> 条申请</span>
        <span class="divider">|</span>
        <span class="text-warning">待审核 <b>{{ statusCount(0) }}</b></span>
        <span class="divider">|</span>
        <span>已通过 <b>{{ statusCount(1) }}</b></span>
        <span class="divider">|</span>
        <span>已驳回 <b>{{ statusCount(2) }}</b></span>
      </div>
    </div>

    <!-- 搜索 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="关键字">
            <el-input
              v-model="query.keyword"
              placeholder="昵称 / 真实姓名 / 手机号"
              clearable
              style="width: 200px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="审核状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 120px">
              <el-option v-for="s in AUTH_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="提交时间">
            <el-date-picker
              v-model="query.dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              style="width: 260px"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button icon="el-icon-download" size="small" @click="handleExport">导出申请</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <!-- 申请人 -->
        <el-table-column label="申请人" min-width="160">
          <template slot-scope="scope">
            <div class="user-cell">
              <span class="user-avatar" :class="{ 'user-avatar--female': scope.row.gender === 2 }">
                {{ (scope.row.nickname || '客')[0] }}
              </span>
              <div class="user-main">
                <span class="user-name">{{ scope.row.nickname || '未设置昵称' }}</span>
                <span class="user-id">编号 {{ scope.row.user_id }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <!-- 真实姓名 -->
        <el-table-column label="真实姓名" width="100">
          <template slot-scope="scope">
            <span class="strong-text">{{ scope.row.real_name || '—' }}</span>
          </template>
        </el-table-column>
        <!-- 证件号码 -->
        <el-table-column label="证件号码" width="170">
          <template slot-scope="scope">
            <span class="mono-text">{{ scope.row.id_card_no || '—' }}</span>
          </template>
        </el-table-column>
        <!-- 证件照片 -->
        <el-table-column label="证件照片" width="130" align="center">
          <template slot-scope="scope">
            <div class="thumb-cell">
              <el-image
                :src="scope.row.id_card_front"
                :preview-src-list="[scope.row.id_card_front, scope.row.id_card_back]"
                fit="cover"
                class="thumb-item"
              />
              <el-image
                :src="scope.row.id_card_back"
                :preview-src-list="[scope.row.id_card_front, scope.row.id_card_back]"
                fit="cover"
                class="thumb-item"
              />
            </div>
          </template>
        </el-table-column>
        <!-- 提交时间 -->
        <el-table-column label="提交时间" width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.created_at }}</template>
        </el-table-column>
        <!-- 审核状态 -->
        <el-table-column label="审核状态" width="95" align="center">
          <template slot-scope="scope">
            <el-tag :type="authStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ authStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 审核结果 -->
        <el-table-column label="审核结果" min-width="170" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.status === 1" class="cell-muted">
              {{ scope.row.audit_at }} 通过
            </span>
            <span v-else-if="scope.row.status === 2" class="reject-text">
              {{ scope.row.reject_reason }}
            </span>
            <span v-else class="cell-muted">待处理</span>
          </template>
        </el-table-column>
        <!-- 操作 -->
        <el-table-column label="操作" width="110" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.status === 0"
              type="text" size="mini"
              @click="handleAudit(scope.row)"
            >审核</el-button>
            <el-button v-else type="text" size="mini" @click="handleAudit(scope.row)">查看</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 审核弹框 -->
    <el-dialog
      :title="isPending ? '实名认证审核' : '认证申请详情'"
      :visible.sync="auditVisible"
      width="700px"
      top="7vh"
      class="audit-dialog"
      append-to-body
    >
      <div v-if="auditRow" class="audit-body">
        <!-- 申请人 -->
        <div class="audit-user">
          <span class="audit-avatar" :class="{ 'user-avatar--female': auditRow.gender === 2 }">
            {{ (auditRow.nickname || '客')[0] }}
          </span>
          <div class="audit-user-main">
            <div class="audit-nick">
              {{ auditRow.nickname || '未设置昵称' }}
              <el-tag :type="authStatusTagType(auditRow.status)" size="mini" effect="plain">
                {{ authStatusText(auditRow.status) }}
              </el-tag>
            </div>
            <div class="audit-sub">
              编号 {{ auditRow.user_id }} · 手机号 {{ auditRow.phone || '未授权' }} · 提交于 {{ auditRow.created_at }}
            </div>
          </div>
        </div>

        <!-- 证件照片 -->
        <div class="audit-photos">
          <div class="photo-item">
            <el-image
              :src="auditRow.id_card_front"
              :preview-src-list="[auditRow.id_card_front, auditRow.id_card_back]"
              fit="contain"
              class="photo-img"
            />
            <div class="photo-caption">证件人像面</div>
          </div>
          <div class="photo-item">
            <el-image
              :src="auditRow.id_card_back"
              :preview-src-list="[auditRow.id_card_front, auditRow.id_card_back]"
              fit="contain"
              class="photo-img"
            />
            <div class="photo-caption">证件国徽面</div>
          </div>
        </div>

        <!-- 填写信息 -->
        <div class="audit-fields">
          <div class="field-row">
            <span class="field-label">真实姓名</span>
            <span class="strong-text">{{ auditRow.real_name || '—' }}</span>
          </div>
          <div class="field-row">
            <span class="field-label">证件号码</span>
            <span class="mono-text">{{ auditRow.id_card_no || '—' }}</span>
          </div>
          <div class="field-row">
            <span class="field-label">性别</span>
            <span>{{ genderText(auditRow.gender) }}</span>
          </div>
          <div class="field-row">
            <span class="field-label">手机号</span>
            <span>{{ auditRow.phone || '未授权' }}</span>
          </div>
          <div class="field-row">
            <span class="field-label">提交时间</span>
            <span>{{ auditRow.created_at }}</span>
          </div>
          <div class="field-row">
            <span class="field-label">审核时间</span>
            <span>{{ auditRow.audit_at || '—' }}</span>
          </div>
        </div>

        <!-- 历史驳回原因 -->
        <div v-if="auditRow.status === 2 && !rejectMode" class="reject-note">
          <i class="el-icon-warning-outline" />
          <span>驳回原因：{{ auditRow.reject_reason || '未填写' }}</span>
        </div>

        <!-- 驳回表单 -->
        <div v-if="rejectMode" class="reject-box">
          <div class="reject-title">请选择或填写驳回原因（将展示给用户）</div>
          <div class="reason-chips">
            <span
              v-for="r in REJECT_REASONS"
              :key="r"
              class="reason-chip"
              :class="{ 'is-active': rejectReason === r }"
              @click="rejectReason = r"
            >{{ r }}</span>
          </div>
          <el-input
            v-model="rejectReason"
            type="textarea"
            :rows="2"
            maxlength="100"
            show-word-limit
            placeholder="补充说明具体问题，便于用户修改后重新提交"
          />
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <template v-if="rejectMode">
          <el-button @click="rejectMode = false">返 回</el-button>
          <el-button type="danger" plain @click="submitReject">确认驳回</el-button>
        </template>
        <template v-else-if="isPending">
          <el-button @click="auditVisible = false">关 闭</el-button>
          <el-button type="danger" plain @click="openReject">驳 回</el-button>
          <el-button type="primary" @click="submitPass">通过认证</el-button>
        </template>
        <template v-else>
          <el-button @click="auditVisible = false">关 闭</el-button>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  AUTH_STATUS, REJECT_REASONS,
  authStatusText, authStatusTagType
} from '@/mock/userAuth'
import { genderText } from '@/mock/user'
import { getUserAuthList, getUserAuthDetail, passUserAuth, rejectUserAuth } from '@/api/user'

export default {
  name: 'UserAuthManage',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      // 头部统计（后端全量计数，不受当页筛选影响）
      statusCounts: {},
      query: { keyword: '', status: '', dateRange: null },
      auditVisible: false,
      auditRow: null,
      rejectMode: false,
      rejectReason: '',
      AUTH_STATUS,
      REJECT_REASONS,
      authStatusText,
      authStatusTagType,
      genderText
    }
  },
  computed: {
    isPending() {
      return !!this.auditRow && this.auditRow.status === 0
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      const params = {
        page: this.page,
        page_size: this.pageSize
      }
      const kw = (this.query.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status
      if (this.query.dateRange && this.query.dateRange.length === 2) {
        params.date_range = this.query.dateRange
      }
      getUserAuthList(params).then(res => {
        if (res.code !== 200) return
        this.list = res.data.list || []
        this.total = res.data.total || 0
        this.statusCounts = res.data.status_counts || {}
      }).finally(() => {
        this.loading = false
      })
    },
    statusCount(val) { return this.statusCounts[String(val)] || 0 },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', status: '', dateRange: null }
      this.page = 1
      this.loadData()
    },
    handleExport() {
      // 导出申请（POST /user/auth/export）规格待确认（同步/异步），后端落地后再接
      this.$message.info('导出申请接口待后端确认导出方案后开放')
    },
    handleAudit(row) {
      this.auditRow = row
      this.rejectMode = false
      this.rejectReason = ''
      this.auditVisible = true
      // 拉最新详情（含证件照片正反面），失败时保留列表行数据
      getUserAuthDetail(row.id).then(res => {
        if (res.code === 200 && res.data && this.auditRow && this.auditRow.id === row.id) {
          this.auditRow = res.data
        }
      })
    },
    openReject() {
      this.rejectMode = true
      this.rejectReason = ''
    },
    submitPass() {
      const row = this.auditRow
      if (!row) return
      this.$confirm(
        `确认通过「${row.real_name || row.nickname}」的实名认证？通过后用户即获得已认证身份。`,
        '通过确认',
        { type: 'warning' }
      ).then(() => {
        passUserAuth(row.id).then(res => {
          if (res.code !== 200) return
          this.auditVisible = false
          this.$message.success('已通过该用户的实名认证')
          this.loadData()
        })
      }).catch(() => {})
    },
    submitReject() {
      const row = this.auditRow
      if (!row) return
      const reason = (this.rejectReason || '').trim()
      if (!reason) {
        this.$message.warning('请先选择或填写驳回原因')
        return
      }
      rejectUserAuth(row.id, { reject_reason: reason }).then(res => {
        if (res.code !== 200) return
        this.rejectMode = false
        this.auditVisible = false
        this.$message.success('已驳回，原因将展示给用户')
        this.loadData()
      })
    }
  }
}
</script>

<style scoped>
/* 申请人单元格 */
.user-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.user-avatar {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #6b5a45;
  color: #fff;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.user-avatar--female {
  background: #a4686b;
}
.user-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.35;
}
.user-name {
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-id {
  font-size: 11px;
  color: #a8abb2;
}
.strong-text {
  color: #303133;
  font-weight: 500;
}
.mono-text {
  font-family: Consolas, Monaco, monospace;
  font-size: 12.5px;
  color: #4d4d4d;
  letter-spacing: 0.2px;
}
.cell-muted {
  color: #a8abb2;
  font-size: 12px;
}
.reject-text {
  color: #c45656;
  font-size: 12px;
}
.text-warning {
  color: #b88230;
}

/* 证件照缩略图 */
.thumb-cell {
  display: flex;
  gap: 6px;
  justify-content: center;
}
.thumb-item {
  width: 52px;
  height: 34px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #ebe5db;
  background: #f6f1e8;
  cursor: pointer;
}

/* 审核弹框 */
.audit-dialog >>> .el-dialog__body {
  padding: 14px 30px 6px;
}
.audit-user {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.audit-avatar {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #6b5a45;
  color: #fff;
  font-size: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.audit-user-main {
  min-width: 0;
}
.audit-nick {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.audit-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.audit-photos {
  display: flex;
  gap: 12px;
}
.photo-item {
  flex: 1;
  min-width: 0;
}
.photo-img {
  width: 100%;
  height: 172px;
  border-radius: 8px;
  background: #f6f1e8;
  border: 1px solid #efe8dd;
}
.photo-caption {
  margin-top: 6px;
  text-align: center;
  font-size: 12px;
  color: #909399;
}
.audit-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 24px;
  margin-top: 14px;
  border-top: 1px solid #f0f0f0;
}
.field-row {
  display: flex;
  padding: 9px 0;
  border-bottom: 1px dashed #f0f0f0;
  font-size: 13px;
  color: #303133;
}
.field-label {
  flex: 0 0 76px;
  color: #909399;
}
.reject-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fdf5f4;
  border: 1px solid #f6d7d3;
  font-size: 13px;
  color: #c45656;
}
.reject-box {
  margin-top: 14px;
  padding: 14px 16px 16px;
  border-radius: 8px;
  background: #fdf5f4;
  border: 1px solid #f6d7d3;
}
.reject-title {
  font-size: 13px;
  color: #8a4a44;
  margin-bottom: 10px;
}
.reason-chips {
  margin-bottom: 4px;
}
.reason-chip {
  display: inline-block;
  padding: 4px 11px;
  margin: 0 8px 8px 0;
  font-size: 12px;
  color: #606266;
  background: #fff;
  border: 1px solid #e8ddd9;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.15s;
}
.reason-chip:hover {
  border-color: #d8bdb6;
  color: #8e1103;
}
.reason-chip.is-active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}
</style>
